import React, { useMemo, useState } from 'react';
import { Tag } from 'lucide-react';
import {
  getDisplayPlanningSignals,
  type PlanningSignalSurface,
} from '../../../lib/planning/tags';

interface PlanningSignalChipsProps {
  application?: Record<string, unknown> | null;
  category?: unknown;
  surface?: PlanningSignalSurface;
  maxVisible?: number;
  className?: string;
  chipClassName?: string;
  showIcon?: boolean;
  showOverflow?: boolean;
  dense?: boolean;
}

export default function PlanningSignalChips({
  application,
  category,
  surface = 'card',
  maxVisible = 2,
  className = '',
  chipClassName = '',
  showIcon = false,
  showOverflow = true,
  dense = false,
}: PlanningSignalChipsProps) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const allSignals = useMemo(
    () => getDisplayPlanningSignals(application ?? undefined, { category, surface }),
    [application, category, surface]
  );
  const signals = useMemo(
    () => (typeof maxVisible === 'number' && maxVisible >= 0
      ? allSignals.slice(0, maxVisible)
      : allSignals),
    [allSignals, maxVisible]
  );
  const overflowSignals = typeof maxVisible === 'number' && maxVisible >= 0
    ? allSignals.slice(maxVisible)
    : [];
  const overflowCount = overflowSignals.length;

  if (signals.length === 0) return null;

  if (surface === 'card') {
    return (
      <div
        className={`flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] leading-none ${className}`}
        aria-label="Planning signals"
      >
        {signals.map((signal) => (
          <span
            key={signal.slug}
            className={`inline-flex min-w-0 max-w-full items-baseline gap-1 ${chipClassName}`}
            title={signal.label}
          >
            <span className="font-semibold text-[var(--c-accent)]">/</span>
            <span className="min-w-0 truncate font-medium text-[var(--c-text-secondary)]">
              {signal.label}
            </span>
          </span>
        ))}
        {showOverflow && overflowCount > 0 ? (
          <span
            className="group/signal-overflow relative inline-flex min-w-0 max-w-full items-baseline gap-1"
            title={`${overflowCount} more signal${overflowCount === 1 ? '' : 's'}`}
            onMouseLeave={() => setOverflowOpen(false)}
          >
            <span className="font-semibold text-[var(--c-accent)]">/</span>
            <button
              type="button"
              className="font-medium text-[var(--c-text-tertiary)] underline-offset-2 transition-colors hover:text-[var(--c-accent)] focus:text-[var(--c-accent)] focus:outline-none focus-visible:underline"
              aria-label={`Show ${overflowCount} more planning signal${overflowCount === 1 ? '' : 's'}`}
              aria-expanded={overflowOpen}
              onClick={(event) => {
                event.stopPropagation();
                setOverflowOpen((current) => !current);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setOverflowOpen(false);
                  event.currentTarget.blur();
                }
              }}
            >
              +{overflowCount}
            </button>
            <span
              className={`absolute left-0 top-full z-50 mt-1 w-max max-w-[14rem] rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-1.5 text-[10px] leading-4 text-[var(--c-text-secondary)] shadow-lg transition duration-150 ${
                overflowOpen
                  ? 'pointer-events-auto opacity-100'
                  : 'pointer-events-none opacity-0 group-hover/signal-overflow:pointer-events-auto group-hover/signal-overflow:opacity-100 group-focus-within/signal-overflow:pointer-events-auto group-focus-within/signal-overflow:opacity-100'
              }`}
              role="tooltip"
            >
              {overflowSignals.map((signal) => (
                <span key={signal.slug} className="block whitespace-nowrap">
                  <span className="font-semibold text-[var(--c-accent)]">/</span>{' '}
                  {signal.label}
                </span>
              ))}
            </span>
          </span>
        ) : null}
      </div>
    );
  }

  const sizeClass = dense
    ? 'h-5 px-2 text-[10px]'
    : 'h-6 px-2.5 text-[11px]';

  return (
    <div className={`flex min-w-0 flex-wrap items-center gap-1.5 ${className}`}>
      {showIcon ? (
        <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-[var(--c-accent)]/20 bg-[var(--c-accent)]/10 text-[var(--c-accent)]">
          <Tag className="h-3 w-3" />
        </span>
      ) : null}
      {signals.map((signal, index) => (
        <span
          key={signal.slug}
          className={`inline-flex max-w-full items-center gap-1 rounded-md border border-[var(--c-accent)]/20 bg-[var(--c-accent)]/[0.08] font-medium leading-none text-[var(--c-text-primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${sizeClass} ${chipClassName}`}
          title={signal.label}
        >
          {index === 0 ? (
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--c-accent)]" />
          ) : null}
          <span className="min-w-0 truncate">{signal.label}</span>
        </span>
      ))}
      {showOverflow && overflowCount > 0 ? (
        <span
          className={`inline-flex items-center rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] font-medium leading-none text-[var(--c-text-secondary)] ${sizeClass}`}
          title={`${overflowCount} more signal${overflowCount === 1 ? '' : 's'}`}
        >
          +{overflowCount}
        </span>
      ) : null}
    </div>
  );
}
